import React from 'react';

import { useFormContext } from 'react-hook-form';

import { Collapse, Divider, Grid, Typography } from '@mui/material';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import ControlledAirportSelect from '../../../components/ControlledAirportSelect';

import { Employee, EmployeePosition, FlightOperationNotification, FormGenerator } from '../../../core/api/clients';

import { useFlightOperationNotificationData } from '../../../core/context/FlightOperationNotificationDataContext';
import { useFlightOperationNotificationFieldOptions } from '../../../core/context/FlightOperationNortificationFieldOptionContext';
import ControlledAircraftSelect from '../../../components/ControlledAircraftSelect';

import { transformToUtcTime } from '../../../core/textTransforms';
import ControlledSelect from '../../../components/ControlledSelect';

interface FlightStepProps {
  visible: boolean,
  readOnly?: boolean,
  isRevenue?: boolean,
  isPriorVersion?: boolean,
}

function FlightStep({ visible, readOnly, isRevenue, isPriorVersion }: FlightStepProps) {
  const { airports, aircrafts } = useFlightOperationNotificationFieldOptions();
  const { flightOperationNotification, allowedActions } = useFlightOperationNotificationData();

  const { control, setValue, resetField } = useFormContext<FlightOperationNotification>();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<FlightOperationNotification>
            name="flightNumber"
            control={control}
            rules={{ required: flightOperationNotification.formGeneratedFrom !== FormGenerator.Blank }}
            readOnly={readOnly || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup}
            disableEditPopper={!allowedActions?.edit || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledSelect<FlightOperationNotification>
            name="mainlinePartner"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
            menuItems={[
              { name: 'AA', value: 'AA' },
              { name: 'ZW', value: 'ZW' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAircraftSelect<FlightOperationNotification>
            name="aircraftNumber"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={aircrafts ?? []}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightOperationNotification>
            name="departureAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightOperationNotification>
            name="scheduledArrivalAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit || flightOperationNotification.formGeneratedFrom === FormGenerator.FlightLookup || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightOperationNotification>
            name="landingAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        {flightOperationNotification.pushTime && (
          <React.Fragment>
            <Grid item xs={2.4} textAlign="center">
              <Typography variant="caption">OUT: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {transformToUtcTime(flightOperationNotification.pushTime)}
              </Typography>
            </Grid>
            <Grid item xs={2.4} textAlign="center">
              <Typography variant="caption">OFF: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {transformToUtcTime(flightOperationNotification.offTime)}
              </Typography>
            </Grid>
            <Grid item xs={2.4} textAlign="center">
              <Typography variant="caption">ETA: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {transformToUtcTime(flightOperationNotification.etaTime)}
              </Typography>
            </Grid>
            <Grid item xs={2.4} textAlign="center">
              <Typography variant="caption">ON: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {transformToUtcTime(flightOperationNotification.onTime)}
              </Typography>
            </Grid>
            <Grid item xs={2.4} textAlign="center">
              <Typography variant="caption">IN: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {transformToUtcTime(flightOperationNotification.inTime)}
              </Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightOperationNotification>
            groupName="captain"
            name="captain"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['captainId', 'captain', 'captainDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('captain', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: flightOperationNotification.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightOperationNotification>
            groupName="firstOfficer"
            name="firstOfficer"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['firstOfficerId', 'firstOfficer', 'firstOfficerDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('firstOfficer', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: flightOperationNotification.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightOperationNotification>
            groupName="flightAttendant"
            name="flightAttendant"
            position={EmployeePosition.InFlight}
            control={control}
            fieldNames={['flightAttendantId', 'flightAttendant', 'flightAttendantDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('flightAttendant', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: isRevenue && flightOperationNotification.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightOperationNotification>
            groupName="dispatcher"
            name="dispatcher"
            position={EmployeePosition.Dispatch}
            control={control}
            fieldNames={['dispatcherId', 'dispatcher', 'dispatcherDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('dispatcher', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('dispatcherId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('dispatcherDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: flightOperationNotification.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit || isPriorVersion}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default FlightStep;
